import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { SupplierLogo } from "@/components/GeschaeftspartnerLogo";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { useCurrentUser, userHasGroup, userIsBuero } from "@/lib/auth";
import { canViewHref } from "@/lib/acl";
import { useRealtime } from "@/lib/useRealtime";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  cancelOrder,
  getOrderById,
  markOrderDelivered,
  restoreOrder,
  unmarkOrderDelivered,
  type OrderHistoryRow,
} from "@/lib/orders";
import {
  fetchBestellwesenOverview,
  type BestellwesenAnfrageOverviewItem,
  type BestellwesenOrderOverviewItem,
  type BestellwesenOverview,
  type BestellwesenOverviewSection,
} from "@/lib/bestellwesenOverview";
import type { MaListItem } from "@/lib/material";
import { MaDialog } from "@/pages/material/MaDialog";
import { AnfrageDetailHost } from "@/pages/anfragen/AnfrageDetailHost";
import {
  getAnfrage,
  getAnfrageMailCandidateCounts,
  type AnfrageDetail,
} from "@/lib/anfragen";
import {
  fmtMaDateOnly,
  formatRequestAge,
  MaFortschrittBadge,
  maAuftragLabel,
  maIsUrgentUnclaimedFix,
} from "@/pages/material/maListDisplay";
import { fmtDateTime, labelAnfrageStatus, statusBadgeClass } from "@/pages/anfragen/anfrageWizardUtils";
import { fmtOrderDate } from "@/pages/bestellungen/orderHistoryDisplay";
import { OrderDetailDialog } from "@/pages/bestellungen/OrderDetailDialog";
import { OrderStornoDialog, type OrderStornoDialogMode } from "@/pages/bestellungen/OrderStornoDialog";
import { useOrderWizard } from "@/pages/bestellungen/useOrderWizard";

function SectionTitle({
  title,
  total,
  tone = "accent",
}: {
  title: string;
  total: number;
  tone?: "accent" | "warn";
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {title}
      {total > 0 ? (
        <span
          className={clsx(
            "rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums text-white",
            tone === "warn" ? "bg-status-orange" : "bg-accent",
          )}
        >
          {total}
        </span>
      ) : null}
    </span>
  );
}

function StatLink({
  href,
  value,
  label,
  tone,
  zeroHint,
}: {
  href: string;
  value: number;
  label: string;
  tone: "accent" | "warn";
  zeroHint: string;
}) {
  const hot = value > 0;
  return (
    <Link
      to={href}
      className={clsx("eh-bw-stat", hot && (tone === "warn" ? "eh-bw-stat--warn" : "eh-bw-stat--accent"))}
    >
      <span
        className={clsx(
          "eh-bw-stat__value",
          !hot && "text-label-tertiary",
          hot && tone === "warn" && "text-status-orange",
          hot && tone === "accent" && "text-accent",
        )}
      >
        {value}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium leading-snug text-label">{label}</span>
        <span className="mt-0.5 block text-xs text-label-tertiary">
          {hot ? "Alle anzeigen" : zeroHint}
        </span>
      </span>
    </Link>
  );
}

function projectDetail(ma: MaListItem): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const value of [
    ma.auftrag_ort,
    ma.auftrag_ort_zusatz,
    ma.auftrag_beschreibung,
    ma.auftrag_kunde,
    ma.auftrag_ansprechpartner,
  ]) {
    const text = value != null ? String(value).trim() : "";
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(text);
  }
  return parts.join(" · ");
}

function AgeChip({ value }: { value: string }) {
  if (!value) return null;
  const aged = value.includes("Tag");
  return (
    <span
      className={clsx(
        "shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
        aged
          ? "bg-status-orange/15 text-status-orange"
          : "bg-fill-tertiary text-label-secondary",
      )}
    >
      {value}
    </span>
  );
}

function SectionLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      to={href}
      className="inline-flex items-center rounded-full border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10"
    >
      {label}
    </Link>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <p className="px-4 py-6 text-center text-sm text-label-secondary">{children}</p>
  );
}

function MaOverviewList({
  items,
  onOpen,
}: {
  items: MaListItem[];
  onOpen: (id: number) => void;
}) {
  if (!items.length) {
    return <EmptyHint>Keine Einträge.</EmptyHint>;
  }
  return (
    <ul className="eh-bw-list">
      {items.map((ma) => {
        const urgentFix = maIsUrgentUnclaimedFix(ma);
        const label = maAuftragLabel(ma);
        const detail = projectDetail(ma);
        const age = formatRequestAge(ma.created_at);
        return (
          <li key={ma.id} className={urgentFix ? "eh-bw-row--urgent" : undefined}>
            <button type="button" className="eh-bw-row eh-bw-row--ma" onClick={() => onOpen(ma.id)}>
              <div className="eh-bw-ma-id flex min-w-0 items-center gap-2">
                <span className="truncate font-mono text-accent">{ma.ma_nummer}</span>
                {urgentFix ? <span className="ma-urgent-fix-pill shrink-0">Sofort</span> : null}
              </div>
              <div className="eh-bw-ma-project min-w-0">
                <div className="truncate text-label">{label}</div>
                {detail ? (
                  <div className="eh-bw-ma-project__place truncate text-label-secondary" title={detail}>
                    {detail}
                  </div>
                ) : null}
              </div>
              <div className="eh-bw-ma-meta text-label-secondary">
                <div>Eingang {fmtMaDateOnly(ma.created_at) || "—"}</div>
                <div>
                  Liefertermin{" "}
                  {ma.liefertermin ? (
                    <span
                      className={
                        urgentFix
                          ? "font-semibold text-status-red"
                          : ma.liefertermin_fix
                            ? "font-semibold text-status-orange"
                            : "text-label"
                      }
                    >
                      {ma.liefertermin_fix ? "Fix " : ""}
                      {fmtMaDateOnly(ma.liefertermin)}
                    </span>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <div className="eh-bw-ma-age">
                <AgeChip value={age} />
              </div>
              <div className="eh-bw-ma-status">
                <MaFortschrittBadge f={ma.monteur_fortschritt} status={ma.status} />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function AnfragenOverviewList({
  items,
  onOpen,
}: {
  items: BestellwesenAnfrageOverviewItem[];
  onOpen: (id: number) => void;
}) {
  if (!items.length) {
    return <EmptyHint>Keine offenen Anfragen.</EmptyHint>;
  }
  return (
    <ul className="eh-bw-list">
      {items.map((row) => {
        const age = formatRequestAge(row.created_at);
        const subject = row.subject || "Ohne Betreff";
        return (
          <li key={row.id}>
            <button type="button" className="eh-bw-row" onClick={() => onOpen(row.id)}>
              <SupplierLogo
                name={row.supplier_name}
                partnerId={row.supplier_gp_id}
                hasLogo={row.supplier_has_logo}
                logoUpdatedAt={row.supplier_logo_updated_at}
                className="h-8 w-8 rounded-lg text-[10px]"
              />
              <div className="eh-bw-row__main">
                <p className="truncate text-sm font-medium text-label" title={subject}>
                  {subject}
                </p>
                <p className="mt-0.5 truncate text-xs text-label-secondary">
                  {row.supplier_name || "Kein Lieferant"}
                  <span className="text-label-tertiary"> · </span>
                  {fmtDateTime(row.created_at)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <AgeChip value={age} />
                <span
                  className={clsx(
                    "inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold",
                    statusBadgeClass(row.status as "draft" | "ready" | "sent"),
                  )}
                >
                  {labelAnfrageStatus(row.status as "draft" | "ready" | "sent")}
                </span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function OrdersOverviewList({
  items,
  staleDays,
  onOpen,
}: {
  items: BestellwesenOrderOverviewItem[];
  staleDays: number;
  onOpen: (id: number) => void;
}) {
  if (!items.length) {
    return (
      <EmptyHint>
        Keine Bestellungen, die länger als {staleDays} Tage offen und nicht als geliefert markiert sind.
      </EmptyHint>
    );
  }
  return (
    <ul className="eh-bw-list">
      {items.map((row) => {
        const title = row.bestellnummern || row.subject || `Bestellung #${row.id}`;
        const age = formatRequestAge(row.created_at) || `${row.days_open} Tage`;
        const severe = row.days_open >= 60;
        return (
          <li key={row.id}>
            <button type="button" className="eh-bw-row" onClick={() => onOpen(row.id)}>
              <SupplierLogo
                name={row.supplier_name}
                partnerId={row.supplier_gp_id}
                hasLogo={row.supplier_has_logo}
                logoUpdatedAt={row.supplier_logo_updated_at}
                className="h-8 w-8 rounded-lg text-[10px]"
              />
              <div className="eh-bw-row__main">
                <p className="truncate font-mono text-[13px] font-semibold text-accent" title={title}>
                  {title}
                </p>
                <p className="mt-0.5 truncate text-xs text-label-secondary">
                  {row.supplier_name || "Kein Lieferant"}
                  <span className="text-label-tertiary"> · </span>
                  bestellt {fmtOrderDate(row.created_at)}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <div
                  className={clsx(
                    "text-sm font-semibold tabular-nums",
                    severe ? "text-status-red" : "text-status-orange",
                  )}
                >
                  {age}
                </div>
                <div className="text-[11px] text-label-tertiary">offen</div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function TruncatedHint<T>({ section }: { section: BestellwesenOverviewSection<T> }) {
  if (!section.truncated) return null;
  return (
    <p className="border-t border-separator px-4 py-2 text-xs text-label-secondary">
      {section.total} Einträge insgesamt – es werden die ältesten {section.items.length} angezeigt.
    </p>
  );
}

export function BestellwesenUebersichtPage() {
  const { user } = useCurrentUser();
  const canMarkDelivered = userIsBuero(user);
  const { openEdit, beginReorder } = useOrderWizard();
  const groups = (user?.groups as string[] | undefined) ?? [];

  const isBueroOrAdmin = useMemo(() => {
    if (!user) return false;
    return (
      userHasGroup(user, "App-Admins") ||
      userHasGroup(user, "Büro") ||
      userHasGroup(user, "App-Tester")
    );
  }, [user]);

  const [overview, setOverview] = useState<BestellwesenOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [canMaterial, setCanMaterial] = useState(false);
  const [canAnfragen, setCanAnfragen] = useState(false);
  const [canBestellungen, setCanBestellungen] = useState(false);

  const [maDialogOpen, setMaDialogOpen] = useState(false);
  const [activeMaId, setActiveMaId] = useState<number | null>(null);

  const [anfrageDetailOpen, setAnfrageDetailOpen] = useState(false);
  const [anfrageDetailLoading, setAnfrageDetailLoading] = useState(false);
  const [anfrageDetail, setAnfrageDetail] = useState<AnfrageDetail | null>(null);
  const [anfrageMailCounts, setAnfrageMailCounts] = useState<Record<string, number>>({});

  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [orderDetail, setOrderDetail] = useState<OrderHistoryRow | null>(null);
  const [stornoDialog, setStornoDialog] = useState<{
    open: boolean;
    mode: OrderStornoDialogMode;
    row: OrderHistoryRow | null;
    loading: boolean;
  }>({ open: false, mode: "cancel", row: null, loading: false });

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      canViewHref("/materialanforderungen.html", groups),
      canViewHref("/anfragen.html", groups),
      canViewHref("/bestellungen.html", groups),
    ]).then(([material, anfragen, bestellungen]) => {
      if (!cancelled) {
        setCanMaterial(material);
        setCanAnfragen(anfragen);
        setCanBestellungen(bestellungen);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [groups]);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      setOverview(await fetchBestellwesenOverview());
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Übersicht konnte nicht geladen werden.";
      setError(message);
      if (!options?.silent) setOverview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime-Änderungen still einpflegen – die Übersicht bleibt dabei stehen.
  useRealtime({
    scopes: ["material-anforderungen:list", "orders:list", "anfragen:list"],
    domains: ["material_anforderungen", "material-anforderungen", "anfragen", "orders"],
    events: [
      "material_anforderungen_changed",
      "material-anforderungen_changed",
      "material_anforderung_changed",
      "anfragen_changed",
      "orders_changed",
    ],
    onChange: () => void reload({ silent: true }),
    fallbackIntervalMs: 180_000,
  });

  function openMa(id: number) {
    setActiveMaId(id);
    setMaDialogOpen(true);
  }

  async function openAnfrage(id: number) {
    setAnfrageDetailOpen(true);
    setAnfrageDetailLoading(true);
    setAnfrageDetail(null);
    try {
      const [row, counts] = await Promise.all([
        getAnfrage(id),
        getAnfrageMailCandidateCounts().catch(() => ({})),
      ]);
      setAnfrageDetail(row);
      setAnfrageMailCounts(counts || {});
    } catch {
      setAnfrageDetailOpen(false);
    } finally {
      setAnfrageDetailLoading(false);
    }
  }

  const openOrder = useCallback(async (id: number) => {
    setOrderDetailOpen(true);
    setOrderDetailLoading(true);
    setOrderDetail(null);
    try {
      const order = await getOrderById(id);
      if (!order) {
        setOrderDetailOpen(false);
        toast.error("Bestellung nicht gefunden.");
        return;
      }
      setOrderDetail(order);
    } catch (err) {
      setOrderDetailOpen(false);
      toast.error(
        err instanceof ApiError ? err.message : "Bestellung konnte nicht geladen werden.",
      );
    } finally {
      setOrderDetailLoading(false);
    }
  }, []);

  const closeOrderDetail = useCallback(() => {
    setOrderDetailOpen(false);
    setOrderDetail(null);
  }, []);

  const openStornoDialog = useCallback((mode: OrderStornoDialogMode, row: OrderHistoryRow) => {
    setStornoDialog({ open: true, mode, row, loading: false });
  }, []);

  const closeStornoDialog = useCallback(() => {
    setStornoDialog((prev) => ({ ...prev, open: false, loading: false }));
  }, []);

  const handleMarkDelivered = useCallback(
    async (row: OrderHistoryRow) => {
      try {
        const result = await markOrderDelivered(row.id);
        toast.success("Bestellung als geliefert markiert.");
        if (result.geliefert_at) {
          setOrderDetail((prev) =>
            prev?.id === row.id ? { ...prev, geliefert_at: result.geliefert_at } : prev,
          );
        }
        await reload();
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Lieferung konnte nicht markiert werden.",
        );
        throw err;
      }
    },
    [reload],
  );

  const handleUnmarkDelivered = useCallback(
    async (row: OrderHistoryRow) => {
      try {
        await unmarkOrderDelivered(row.id);
        toast.success("Liefer-Markierung wurde zurückgenommen.");
        setOrderDetail((prev) =>
          prev?.id === row.id ? { ...prev, geliefert_at: null } : prev,
        );
        await reload();
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Rücknahme der Lieferung fehlgeschlagen.",
        );
        throw err;
      }
    },
    [reload],
  );

  const handleStornoConfirm = useCallback(async () => {
    const { mode, row } = stornoDialog;
    if (!row) return;
    setStornoDialog((prev) => ({ ...prev, loading: true }));
    try {
      if (mode === "cancel") {
        await cancelOrder(row.id);
        toast.success("Bestellung wurde storniert.");
      } else {
        await restoreOrder(row.id);
        toast.success("Stornierung wurde aufgehoben.");
      }
      closeStornoDialog();
      closeOrderDetail();
      await reload();
    } catch (err) {
      setStornoDialog((prev) => ({ ...prev, loading: false }));
      toast.error(
        err instanceof ApiError
          ? err.message
          : mode === "cancel"
            ? "Stornierung fehlgeschlagen."
            : "Aufheben der Stornierung fehlgeschlagen.",
      );
    }
  }, [stornoDialog, closeStornoDialog, closeOrderDetail, reload]);

  const staleDays = overview?.stale_order_days ?? 14;

  return (
    <AppShell
      title="Übersicht"
      subtitle="Offene Materialanforderungen, Anfragen und überfällige Bestellungen auf einen Blick."
    >
      {error ? <Alert variant="error">{error}</Alert> : null}

      {loading && !overview ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Lädt Übersicht …</span>
        </div>
      ) : null}

      {overview ? (
        <div className="space-y-4">
          <div className="eh-bw-stats">
            {canMaterial ? (
              <StatLink
                href="/bestellwesen/materialanforderungen"
                value={overview.offene_materialanforderungen.total}
                label="Offene Anforderungen"
                tone="accent"
                zeroHint="Nichts offen"
              />
            ) : null}
            {canMaterial ? (
              <StatLink
                href="/bestellwesen/materialanforderungen"
                value={overview.materialanforderungen_in_bearbeitung.total}
                label="In Bearbeitung"
                tone="accent"
                zeroHint="Nichts in Arbeit"
              />
            ) : null}
            {canAnfragen && isBueroOrAdmin ? (
              <StatLink
                href="/bestellwesen/anfragen"
                value={overview.offene_anfragen.total}
                label="Offene Anfragen"
                tone="accent"
                zeroHint="Nichts offen"
              />
            ) : null}
            {canBestellungen ? (
              <StatLink
                href="/bestellwesen/bestellungen"
                value={overview.ueberfaellige_bestellungen.total}
                label="Überfällige Bestellungen"
                tone="warn"
                zeroHint="Nichts überfällig"
              />
            ) : null}
          </div>

          {canMaterial && overview.offene_materialanforderungen.total > 0 ? (
            <Card
              title={
                <SectionTitle
                  title="Offene Materialanforderungen"
                  total={overview.offene_materialanforderungen.total}
                />
              }
              actions={
                <SectionLink href="/bestellwesen/materialanforderungen" label="Alle anzeigen" />
              }
              bodyClassName="!p-0"
            >
              <MaOverviewList
                items={overview.offene_materialanforderungen.items}
                onOpen={openMa}
              />
              <TruncatedHint section={overview.offene_materialanforderungen} />
            </Card>
          ) : null}

          {canMaterial && overview.materialanforderungen_in_bearbeitung.total > 0 ? (
            <Card
              title={
                <SectionTitle
                  title={
                    isBueroOrAdmin
                      ? "Meine Materialanforderungen in Bearbeitung"
                      : "Materialanforderungen in Bearbeitung"
                  }
                  total={overview.materialanforderungen_in_bearbeitung.total}
                />
              }
              actions={
                <SectionLink href="/bestellwesen/materialanforderungen" label="Alle anzeigen" />
              }
              bodyClassName="!p-0"
            >
              <MaOverviewList
                items={overview.materialanforderungen_in_bearbeitung.items}
                onOpen={openMa}
              />
              <TruncatedHint section={overview.materialanforderungen_in_bearbeitung} />
            </Card>
          ) : null}

          {canAnfragen && isBueroOrAdmin && overview.offene_anfragen.total > 0 ? (
            <Card
              title={
                <SectionTitle
                  title="Offene Anfragen"
                  total={overview.offene_anfragen.total}
                />
              }
              actions={<SectionLink href="/bestellwesen/anfragen" label="Alle anzeigen" />}
              description="Gesendet oder bereit, noch ohne Bestellung."
              bodyClassName="!p-0"
            >
              <AnfragenOverviewList
                items={overview.offene_anfragen.items}
                onOpen={(id) => void openAnfrage(id)}
              />
              <TruncatedHint section={overview.offene_anfragen} />
            </Card>
          ) : null}

          {canBestellungen && overview.ueberfaellige_bestellungen.total > 0 ? (
            <Card
              className="eh-bw-section--warn"
              title={
                <SectionTitle
                  title="Überfällige Bestellungen"
                  total={overview.ueberfaellige_bestellungen.total}
                  tone="warn"
                />
              }
              actions={<SectionLink href="/bestellwesen/bestellungen" label="Zu Bestellungen" />}
              description={`Nicht als geliefert markiert und älter als ${staleDays} Tage.`}
              bodyClassName="!p-0"
            >
              <OrdersOverviewList
                items={overview.ueberfaellige_bestellungen.items}
                staleDays={staleDays}
                onOpen={(id) => void openOrder(id)}
              />
              <TruncatedHint section={overview.ueberfaellige_bestellungen} />
            </Card>
          ) : null}
        </div>
      ) : null}

      <MaDialog
        open={maDialogOpen}
        maId={activeMaId}
        canProcess={isBueroOrAdmin}
        onClose={() => {
          setMaDialogOpen(false);
          setActiveMaId(null);
        }}
        onChanged={() => void reload()}
      />

      <AnfrageDetailHost
        open={anfrageDetailOpen}
        loading={anfrageDetailLoading}
        detail={anfrageDetail}
        mailCounts={anfrageMailCounts}
        onClose={() => {
          setAnfrageDetailOpen(false);
          setAnfrageDetail(null);
        }}
        onReloadDetail={() => {
          if (anfrageDetail?.id) void openAnfrage(anfrageDetail.id);
        }}
        onChanged={() => void reload()}
      />

      <OrderDetailDialog
        open={orderDetailOpen}
        loading={orderDetailLoading}
        order={orderDetail}
        canMarkDelivered={canMarkDelivered}
        onClose={closeOrderDetail}
        onOrderChange={(next) => {
          setOrderDetail(next);
          void reload({ silent: true });
        }}
        onReorderAll={(row) => {
          closeOrderDetail();
          beginReorder(row, null);
        }}
        onReorderOne={(row, idx) => {
          closeOrderDetail();
          beginReorder(row, [idx]);
        }}
        onEdit={(row) => {
          closeOrderDetail();
          void openEdit(row);
        }}
        onRequestCancel={(row) => {
          closeOrderDetail();
          openStornoDialog("cancel", row);
        }}
        onRequestRestore={(row) => {
          closeOrderDetail();
          openStornoDialog("restore", row);
        }}
        onMarkDelivered={handleMarkDelivered}
        onUnmarkDelivered={handleUnmarkDelivered}
      />
      <OrderStornoDialog
        open={stornoDialog.open}
        mode={stornoDialog.mode}
        row={stornoDialog.row}
        loading={stornoDialog.loading}
        onClose={closeStornoDialog}
        onConfirm={() => void handleStornoConfirm()}
      />
    </AppShell>
  );
}
